import React, { useMemo, useState } from 'react'
import { useParamountAuth } from 'context/ParamountAuthContext'
import {
  getClients,
  getProjectSites,
  getSiteChecklistsForCompany,
  regenerateChecklistPdf,
  fetchSiteChecklistPdf,
} from '../../../services/clients.svc'
import { mediumTime } from 'common/constants'
import './RegenerateChecklistPage.scss'

// Recovery tool for checklists whose PDF was never generated after submission
// (pdfBlobKey left null due to the fire-and-forget generation step failing).
// SuperUser only — accessible only via direct URL, no nav link.
export default function RegenerateChecklistPage() {
  const { userRoles } = useParamountAuth()
  const isSuperUser = (userRoles || []).includes('SuperUser')

  const [companies, setCompanies] = useState([])
  const [companiesLoading, setCompaniesLoading] = useState(false)
  const [companiesLoaded, setCompaniesLoaded] = useState(false)
  const [selectedCompanyId, setSelectedCompanyId] = useState('')

  const [sites, setSites] = useState([])
  const [sitesLoading, setSitesLoading] = useState(false)
  const [selectedSiteId, setSelectedSiteId] = useState('')

  const [checklists, setChecklists] = useState([])
  const [checklistsLoading, setChecklistsLoading] = useState(false)

  const [statusFilter, setStatusFilter] = useState('missing') // missing | all
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const [rowState, setRowState] = useState({}) // checklistId -> { loading, error, success }
  const [error, setError] = useState('')

  const loadCompanies = async () => {
    if (companiesLoaded || companiesLoading) return
    setCompaniesLoading(true)
    setError('')
    try {
      const result = await getClients()
      setCompanies(result || [])
      setCompaniesLoaded(true)
    } catch (err) {
      setError(err?.message || 'Failed to load clients.')
    } finally {
      setCompaniesLoading(false)
    }
  }

  const handleCompanyChange = async (companyId) => {
    setSelectedCompanyId(companyId)
    setSelectedSiteId('')
    setSites([])
    setChecklists([])
    setError('')
    if (!companyId) return

    setSitesLoading(true)
    setChecklistsLoading(true)
    try {
      const [siteResult, checklistResult] = await Promise.all([
        getProjectSites(companyId),
        getSiteChecklistsForCompany(companyId),
      ])
      setSites(siteResult || [])
      setChecklists(checklistResult || [])
    } catch (err) {
      setError(err?.message || 'Failed to load sites/checklists for this company.')
    } finally {
      setSitesLoading(false)
      setChecklistsLoading(false)
    }
  }

  const filteredChecklists = useMemo(() => {
    return (checklists || []).filter((c) => {
      if (selectedSiteId && String(c.site?._id || c.site) !== String(selectedSiteId)) {
        return false
      }
      if (statusFilter === 'missing' && c.pdfBlobKey) {
        return false
      }
      const createdAt = c.createdAt ? new Date(c.createdAt) : null
      if (dateFrom && createdAt && createdAt < new Date(dateFrom)) {
        return false
      }
      if (dateTo && createdAt) {
        const to = new Date(dateTo)
        to.setHours(23, 59, 59, 999)
        if (createdAt > to) return false
      }
      return true
    })
  }, [checklists, selectedSiteId, statusFilter, dateFrom, dateTo])

  const setRow = (checklistId, patch) => {
    setRowState((prev) => ({ ...prev, [checklistId]: { ...prev[checklistId], ...patch } }))
  }

  if (!isSuperUser) {
    return <div className="regenerate-checklist-page">Unauthorized</div>
  }

  const handleRegenerate = async (checklistId) => {
    setRow(checklistId, { loading: true, error: '', success: false })
    try {
      const result = await regenerateChecklistPdf(selectedCompanyId, checklistId)
      setChecklists((prev) =>
        prev.map((c) => (c._id === checklistId ? { ...c, pdfBlobKey: result?.pdfBlobKey || `checklist/${checklistId}.pdf` } : c))
      )
      setRow(checklistId, { loading: false, success: true })
    } catch (err) {
      setRow(checklistId, { loading: false, error: err?.message || 'Failed to regenerate PDF' })
    }
  }

  const handleDownload = async (checklistId) => {
    setRow(checklistId, { downloading: true })
    try {
      const res = await fetchSiteChecklistPdf(selectedCompanyId, checklistId)
      if (!res.ok) throw new Error('PDF not available')
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = `site-inspection-checklist-${checklistId}.pdf`
      a.click()
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000)
    } catch (err) {
      setRow(checklistId, { error: err?.message || 'Failed to download PDF' })
    } finally {
      setRow(checklistId, { downloading: false })
    }
  }

  const missingCount = (checklists || []).filter((c) => !c.pdfBlobKey).length

  return (
    <div className="regenerate-checklist-page">
      <div className="regenerate-checklist-title">Regenerate Checklist PDF</div>
      <div className="regenerate-checklist-subtitle">
        Recovery tool for site inspection checklists whose PDF failed to generate at submission time.
        Only SuperUsers can access this page.
      </div>

      <div className="regenerate-checklist-filters">
        <div className="filter-group">
          <label>Company</label>
          <select
            value={selectedCompanyId}
            onFocus={loadCompanies}
            onChange={(e) => handleCompanyChange(e.target.value)}
          >
            <option value="">{companiesLoading ? 'Loading…' : 'Select a company…'}</option>
            {companies.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Site / Job No.</label>
          <select
            value={selectedSiteId}
            onChange={(e) => setSelectedSiteId(e.target.value)}
            disabled={!selectedCompanyId || sitesLoading}
          >
            <option value="">All sites</option>
            {sites.map((s) => (
              <option key={s._id} value={s._id}>
                {s.siteNumber ? `${s.siteNumber} — ${s.address}` : s.address}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="missing">Missing PDF only</option>
            <option value="all">All submitted checklists</option>
          </select>
        </div>

        <div className="filter-group">
          <label>From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>

        <div className="filter-group">
          <label>To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
      </div>

      {error && <div className="regenerate-checklist-error">{error}</div>}

      {selectedCompanyId && (
        <div className="regenerate-checklist-summary">
          {checklistsLoading
            ? 'Loading checklists…'
            : `${filteredChecklists.length} checklist(s) shown · ${missingCount} missing PDF for this company`}
        </div>
      )}

      {selectedCompanyId && !checklistsLoading && (
        <div className="regenerate-checklist-table-wrapper">
          <table className="regenerate-checklist-table">
            <thead>
              <tr>
                <th>Job No.</th>
                <th>Site Address</th>
                <th>Prepared By</th>
                <th>Submitted</th>
                <th>PDF Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredChecklists.map((c) => {
                const row = rowState[c._id] || {}
                return (
                  <tr key={c._id}>
                    <td>{c.site?.siteNumber || '—'}</td>
                    <td>{c.site?.address || c.siteAddress || '—'}</td>
                    <td>{c.preparedBy || '—'}</td>
                    <td>{c.submittedAt ? mediumTime.format(new Date(c.submittedAt)) : '—'}</td>
                    <td>
                      {c.pdfBlobKey ? (
                        <span className="pdf-status pdf-status-ok">✓ Available</span>
                      ) : (
                        <span className="pdf-status pdf-status-missing">✗ Missing</span>
                      )}
                    </td>
                    <td className="regenerate-checklist-actions">
                      <button
                        className="action-btn action-btn-regenerate"
                        onClick={() => handleRegenerate(c._id)}
                        disabled={row.loading}
                      >
                        {row.loading ? 'Regenerating…' : c.pdfBlobKey ? '↻ Regenerate PDF' : '⚙ Generate PDF'}
                      </button>
                      <button
                        className="action-btn action-btn-download"
                        onClick={() => handleDownload(c._id)}
                        disabled={!c.pdfBlobKey || row.downloading}
                      >
                        {row.downloading ? 'Downloading…' : '⬇ Download PDF'}
                      </button>
                      {row.error && <div className="row-error">{row.error}</div>}
                      {row.success && <div className="row-success">✓ Regenerated successfully</div>}
                    </td>
                  </tr>
                )
              })}
              {!filteredChecklists.length && (
                <tr>
                  <td colSpan={6} className="regenerate-checklist-empty">
                    No checklists match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
